import { Dispatch } from 'redux';
import { DepartmentModel, CurrencyModel, Regions, DepartmentLandingModel } from '@/interface';
import { api } from '@/api/Api';
import pm from "@/core/helpers/ProcessManager";
import { actions as headerActions } from '@/redux/modules/Header';
import { isRateChannelDepartment } from '@/core/helpers/PageHelpers';

export const SET_DEPARTMENT = '@@departmentReducer/SET_DEPARTMENT';
export const SET_ACTIVE_DEPARTMENT_ID = '@@departmentReducer/SET_ACTIVE_DEPARTMENT_ID';
export const SET_DEPARTMENT_LANDING = '@@departmentReducer/SET_DEPARTMENT_LANDING';
export const SET_ACTIVE_DEPARTMENT_LANDING_ADDRESS = '@@departmentReducer/SET_ACTIVE_DEPARTMENT_LANDING_ADDRESS';
export const SET_ACTIVE_CURRENCY_ID = '@@departmentReducer/SET_ACTIVE_CURRENCY_ID';
export const SET_ACTIVE_REGION = '@@departmentReducer/SET_ACTIVE_REGION';
export const SET_NEW_CURRENCY = '@@departmentReducer/SET_NEW_CURRENCY';
export const RESET_NEW_CURRENCY = '@@departmentReducer/RESET_NEW_CURRENCY';
export const SET_ACTIVE_KASSA_ID = '@@departmentReducer/SET_ACTIVE_KASSA_ID';
export const RESET_DEPARTMENT = '@@departmentReducer/RESET_DEPARTMENT';

export type DepartmentAction =
    | ReturnType<typeof resetDepartmentReducer>
    | ReturnType<typeof setDepartments>
    | ReturnType<typeof setDepartmentsLanding>
    | ReturnType<typeof setActiveDepartmentId>
    | ReturnType<typeof setActiveDepartmentLandingAddress>
    | ReturnType<typeof setActiveCurrencyId>
    | ReturnType<typeof setActiveRegion>
    | ReturnType<typeof setNewCurrency>
    | ReturnType<typeof resetNewCurrency>
    | ReturnType<typeof setActiveKassaId>;

export const resetDepartmentReducer = () => ({
    type: RESET_DEPARTMENT as typeof RESET_DEPARTMENT
});

export const setDepartments = (departmentList: DepartmentModel[],) => {
    // Отделения-каналы (курсы мобилки и ecash.kz) в общие списки не кладём: их
    // видят только экраны, которые явно берут selectChannelDepartments
    const branches = departmentList?.filter((department: DepartmentModel) => !isRateChannelDepartment(department));

    return {
        type: SET_DEPARTMENT as typeof SET_DEPARTMENT,
        payload: {
            departmentList: branches?.filter((department: DepartmentModel) => department.isActive === 1),
            allDepartments: branches?.filter((department: DepartmentModel) => department?.id !== 6 && department?.id !== 12),
            channelDepartments: departmentList?.filter((department: DepartmentModel) => isRateChannelDepartment(department) && department.isActive === 1) || [],
        },
    };
};

export const setDepartmentsLanding = (departmentList: DepartmentLandingModel[]) => ({
    type: SET_DEPARTMENT_LANDING as typeof SET_DEPARTMENT_LANDING,
    payload: {
        departmentList
    }
});

export const setActiveDepartmentId = (departmentId: number,) => ({
    type: SET_ACTIVE_DEPARTMENT_ID as typeof SET_ACTIVE_DEPARTMENT_ID,
    payload: {
        departmentId,
    },
});

export const setActiveDepartmentLandingAddress = (departmentAddress: string) => ({
    type: SET_ACTIVE_DEPARTMENT_LANDING_ADDRESS as typeof SET_ACTIVE_DEPARTMENT_LANDING_ADDRESS,
    payload: {
        departmentAddress
    }
});

export const setActiveCurrencyId = (departmentId: number, currencyId: number) => ({
    type: SET_ACTIVE_CURRENCY_ID as typeof SET_ACTIVE_CURRENCY_ID,
    payload: {
        currencyId,
        departmentId
    },
});

export const setActiveRegion = (region: Regions) => ({
    type: SET_ACTIVE_REGION as typeof SET_ACTIVE_REGION,
    payload: { region }
});

export const setNewCurrency = (currency: CurrencyModel) => ({
    type: SET_NEW_CURRENCY as typeof SET_NEW_CURRENCY,
    payload: { newCurrency: currency }
});

export const resetNewCurrency = () => ({
    type: RESET_NEW_CURRENCY as typeof RESET_NEW_CURRENCY
});

export const setActiveKassaId = (kassaId: number,) => ({
    type: SET_ACTIVE_KASSA_ID as typeof SET_ACTIVE_KASSA_ID,
    payload: { kassaId, }
});

/**
 * Отделение кассира задаёт QR-сессия на бэке, а не выбор в интерфейсе:
 * isAuthToday возвращает depId, к которому привязан сегодняшний вход.
 *
 * Раньше этот depId применялся ровно один раз - в navigateByRole при логине. Но
 * первый за день вход идёт через validateOtp, а после F5 отделение поднимается
 * только из localStorage, поэтому activeDepartmentId вполне мог остаться нулевым.
 * SelectDepartments в этом случае молча подставлял первое отделение из списка,
 * оно уезжало в localStorage, и "Открыть кассу" открывала кассу чужого отделения.
 * Поэтому depId применяем при каждой проверке сессии.
 */
export const syncSessionDepartment = (depId?: number) => (dispatch: Dispatch<any>, getState: () => any) => {
    if (!depId) {
        return;
    }

    const state = getState();

    // Кассир и табло жёстко привязаны к одному отделению. Главный кассир,
    // казначей и остальные переключают отделения руками - им сюда лезть нельзя.
    if (!state?.header?.rolesData?.isCashier && !state?.header?.rolesData?.isTablo) {
        return;
    }

    if (state?.header?.activeDepartmentId !== depId) {
        dispatch(headerActions.updateActiveDepartmentId(depId));
    }

    if (state?.departmentReducer?.activeDepartmentId !== depId) {
        dispatch(setActiveDepartmentId(depId));

        // Касса из прошлого отделения к новому не относится. Обнуляем, а не
        // подставляем первую: список отделений мог ещё не приехать, и
        // ensureActiveKassa в SelectDepartments выберет кассу сам, когда будет из чего.
        dispatch(setActiveKassaId(0));
    }
};

export const loadDepartmentsLanding = () => async (dispatch: Dispatch<DepartmentAction>) => {
    pm.openBackDrop();

    api.department.getDepartmentsLanding()
        .then((res) => {
            const departmentList = res?.filter((department: DepartmentModel) => department.isActive === 1);
            for (const department of departmentList) {

                department.currencies.sort(
                    (a: CurrencyModel, b: CurrencyModel) => {
                        const priority = ['USD', 'RUB', 'EUR', 'CNY'];
                        const goldPriority = ['GOLD1', 'GOLD5', 'GOLD10', 'GOLD20', 'GOLD50', 'GOLD100'];

                        const aPriority = priority.indexOf(a.code);
                        const bPriority = priority.indexOf(b.code);

                        if (aPriority !== -1 && bPriority !== -1) {
                            return aPriority - bPriority;
                        } else if (aPriority !== -1) {
                            return -1;
                        } else if (bPriority !== -1) {
                            return 1;
                        }

                        const aGoldPriority = goldPriority.indexOf(a.code);
                        const bGoldPriority = goldPriority.indexOf(b.code);

                        if (aGoldPriority !== -1 && bGoldPriority !== -1) {
                            return aGoldPriority - bGoldPriority;
                        } else if (aGoldPriority !== -1) {
                            return 1;
                        } else if (bGoldPriority !== -1) {
                            return -1;
                        }

                        return 0;
                    }
                );
            }
            dispatch(setDepartmentsLanding(departmentList));
        })
        .catch(() => {
            pm.openSnackBar("Произошла ошибка: Не удалось получить данные по отеделениям", { severity: "error" });
        })
        .finally(() => pm.closeBackDrop());
};